import BaseLasStrategy from './BaseLasStrategy';
import { ColorScheme, toCssColorScheme } from './constants';
import lasRunner from './LasRunnerUtil';
import { listenForExitMessages } from './utils';
import { listenForExternalFlows } from './utils/launchInIframe';
import { hideLoader } from './utils/loader';
import ScLoanError, { lasErrorInstanceMap } from './utils/scLoanError';

// keeping iframeElement oustide function to check if dashboard is being opened more than once
let iframeElem: HTMLIFrameElement;
const iframeId = 'lamf-dashboard-iframe';
let overlayDiv: HTMLDivElement;
let stopExternalFlows: () => void;

type ResizeMessage = {
  type: 'UPDATE_FRAME_HEIGHT';
  payload: DOMRect;
};
type ToggleOverlayMessage = {
  type: 'SHOW_OVERLAY' | 'HIDE_OVERLAY';
};
type DashboardMessage = ResizeMessage | ToggleOverlayMessage;

const dashboardMessageHandler = async (e: MessageEvent<DashboardMessage>) => {
  const { data } = e;
  // ignore non dashboard messages
  if (e.source !== iframeElem.contentWindow) return;
  if (data.type === 'UPDATE_FRAME_HEIGHT') {
    iframeElem.style.height = `${data.payload.height}px`;
  }
  if (data.type === 'SHOW_OVERLAY') {
    overlayDiv.style.display = 'block';
    iframeElem.style.zIndex = '2147483646';
  }
  if (data.type === 'HIDE_OVERLAY') {
    overlayDiv.style.display = 'none';
    iframeElem.style.zIndex = '';
  }
};

const removeIframe = () => {
  if (iframeElem) {
    iframeElem.parentNode?.removeChild(iframeElem);
    window.removeEventListener('message', dashboardMessageHandler);
    stopExternalFlows?.();
    stopExternalFlows = undefined;
    iframeElem = undefined;
  }
  if (overlayDiv) {
    overlayDiv.parentNode?.removeChild(overlayDiv);
    overlayDiv = undefined;
  }
};

const injectIframe = (
  containerElem: HTMLElement,
  url: string,
  colorScheme?: ColorScheme,
) => {
  // calling this method after navigating away can lead to reference being present but element not in dom
  if (iframeElem) {
    removeIframe();
  }
  iframeElem = document.createElement('iframe');
  iframeElem.id = iframeId;
  iframeElem.style.cssText = `min-width:100%;min-height:100vh;position:relative;background:white;outline:0;border:0;padding:0;margin:0;z-index:auto;`;
  iframeElem.style.colorScheme = toCssColorScheme(colorScheme);
  iframeElem.src = url;
  const pageLoadPromise = new Promise<void>((res) => {
    iframeElem.onload = () => res();
  });

  overlayDiv = document.createElement('div');
  overlayDiv.style.cssText = `position:fixed;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.8);z-index:2147483645;display:none;`;

  window.addEventListener('message', dashboardMessageHandler);

  containerElem.append(overlayDiv);
  containerElem.append(iframeElem);

  return pageLoadPromise;
};

class OpenDashboardStrategy extends BaseLasStrategy {
  private containerElem: HTMLElement;

  constructor(
    interactionToken: string,
    targetElemId,
    options: { signal?: AbortSignal; colorScheme?: ColorScheme } = {},
  ) {
    super(interactionToken, options);
    this.containerElem = document.getElementById(targetElemId);

    if (!this.containerElem) throw lasErrorInstanceMap.ELEMENT_NOT_FOUND;
    if (iframeElem && document.body.contains(iframeElem))
      throw lasErrorInstanceMap.DASHBOARD_ALREADY_OPEN;
  }

  async runInteraction() {
    // inject iframe
    const pageLoadPromise = injectIframe(
      this.containerElem,
      this.getLaunchUrl(),
      this.colorScheme,
    );
    // setup for listeners to resolve on postmessage from las_client
    stopExternalFlows = listenForExternalFlows(iframeElem, this.colorScheme);
    const listenerPromise = listenForExitMessages(iframeElem, this.signal);
    // an already-aborted or raced signal can reject this before we await it
    // below; attach a handler now so the rejection never floats unhandled. The
    // real handling still happens at the `await listenerPromise` try/catch.
    listenerPromise.catch(() => {});

    // wait for iframe to load
    await pageLoadPromise;

    // close loader
    hideLoader();

    let clientMessage;
    try {
      // wait for listeners to get some message from client
      clientMessage = await listenerPromise;
    } catch (e) {
      // on abort (or any error) tear down the iframe, mirroring exitInteraction
      removeIframe();
      throw e;
    }
    removeIframe();

    const clientPayload = clientMessage.data.payload;
    if (clientPayload.code !== 0) {
      throw new ScLoanError(clientPayload.message, clientPayload.code);
    }
  }

  public exitInteraction() {
    removeIframe();
    return this.handleInteractionError(lasErrorInstanceMap.USER_CANCELLED);
  }
}

type OpenDashboardArgs = {
  interactionToken?: string;
  target?: string;
  /**
   * Optional AbortSignal to cancel the open dashboard.
   *
   * On abort the SDK confirms the final state with the backend (the same flow
   * as calling `close()`). In the typical cancellation case the task promise
   * REJECTS with a `ScLoanError` whose `.data` holds the interaction status, so
   * handle it in `.catch`. It resolves only if the backend reports the
   * interaction actually completed.
   *
   * When a signal is passed, `service()` returns the bare task promise (the
   * signal is the cancel handle). With no signal it returns the legacy
   * `{ close, task }` object.
   */
  signal?: AbortSignal;
  /** `dark` | `light` | `system`, forwarded verbatim as the `theme` query param */
  colorScheme?: ColorScheme;
};
// when a signal is passed the caller cancels via the signal, so we return the
// bare task promise; otherwise we keep the legacy { close, task } handle
function openDashboard(
  args: OpenDashboardArgs & { signal: AbortSignal },
): ReturnType<typeof lasRunner>;
function openDashboard(args?: OpenDashboardArgs): {
  close: () => Promise<any>;
  task: ReturnType<typeof lasRunner>;
};
function openDashboard({
  interactionToken,
  target,
  signal,
  colorScheme,
}: OpenDashboardArgs = {}) {
  const intentStrategy = new OpenDashboardStrategy(interactionToken, target, {
    signal,
    colorScheme,
  });
  const task = lasRunner(intentStrategy);
  if (signal) {
    return task;
  }
  return {
    close: intentStrategy.exitInteraction.bind(intentStrategy),
    task,
  };
}

export default openDashboard;
